/**
 * Choosing which blurred picture stands in for an image that does not exist yet.
 *
 * ⚠️ THIS IS AN EDITING AID AND IT LIVES IN `@ksa/ui` FOR THAT REASON. §3 says `@ksa/ui` is the
 * builder's own chrome and never renders customer content — which is exactly what this is. The
 * picture is painted by `editor.css` over the box `@ksa/render` emitted; the renderer is not told,
 * cannot be told, and so a published page cannot inherit it. That is the same arrangement as the
 * empty-block label, and it exists because an aid that lives in the renderer is an aid that ships.
 *
 * ⚠️ THE MATCH IS DETERMINISTIC AND OFFLINE. No network, no model, no cache to invalidate: the whole
 * gallery is ten inlined thumbnails, and the choice is a word-overlap score against the description
 * the model already wrote. A round trip to pick a decorative blur would be latency and cost spent on
 * something nobody looks at directly.
 */

import { GALLERY_IDS } from './gallery.ids.ts'

/**
 * ⚠️ NO `src`. A placeholder is now an IDENTITY — which picture was chosen — and the pixels are
 * fetched separately by whoever is going to paint them. They were on this object, and that one field
 * pulled 300 kB of base64 into every bundle that so much as mentioned the matcher, including the
 * marketing page, which has no placeholders on it at all.
 */
export type Placeholder = { id: string; tags: string[]; tagsAr: string[] }

/**
 * Which words send a description to which picture.
 *
 * ⚠️ THESE ARE THE WORDS A DESCRIPTION ACTUALLY USES, NOT A TAXONOMY. A screenshot caught the
 * difference: "a plate of grilled fish and rice" matched NOTHING, because the restaurant entry was
 * tagged with the category words a person would pick — `restaurant`, `cuisine`, `dining` — and none
 * of them appear in a sentence describing an actual plate of food. Tags are added by reading real
 * descriptions and noticing what they say.
 *
 * ⚠️ AND THEY LIVE HERE, NOT WITH THE PICTURES. They are hand-edited and the pictures are generated;
 * keeping them together meant changing one word cost an image API key and ten new photographs.
 *
 * Order is preference: a tie goes to whichever appears first below.
 */
const TAGS: Record<string, string[]> = {
  coffee: ['coffee', 'cafe', 'café', 'espresso', 'roastery', 'bakery', 'tea', 'drink', 'drinks', 'barista', 'cup', 'cups', 'counter', 'beans', 'latte', 'mug', 'brew'],
  restaurant: ['restaurant', 'food', 'meal', 'dish', 'dishes', 'dining', 'kitchen', 'catering', 'menu', 'cuisine', 'lunch', 'dinner', 'plate', 'plates', 'fish', 'rice', 'chicken', 'meat', 'lamb', 'grill', 'grilled', 'bread', 'dessert', 'sweets', 'juice', 'breakfast', 'table', 'platter', 'kabsa'],
  retail: ['shop', 'store', 'retail', 'product', 'products', 'accessories', 'phone', 'mobile', 'electronics', 'shelf', 'shelves', 'boutique', 'display', 'packaging', 'box', 'boxes', 'case', 'cases', 'cover', 'covers', 'charger', 'stock'],
  car: ['car', 'cars', 'vehicle', 'automotive', 'garage', 'rental', 'transport', 'delivery', 'driving', 'wheel', 'tyre', 'tire', 'engine', 'parking', 'road', 'street', 'taxi'],
  tower: ['office', 'business', 'corporate', 'company', 'consulting', 'finance', 'agency', 'riyadh', 'city', 'tower', 'professional', 'skyline', 'meeting', 'desk', 'team', 'startup', 'service', 'services'],
  diriyah: ['heritage', 'culture', 'tradition', 'traditional', 'history', 'historic', 'museum', 'tourism', 'diriyah', 'authentic', 'mud', 'brick', 'walls', 'old', 'town', 'village', 'fort'],
  greenery: ['garden', 'plants', 'landscape', 'nature', 'green', 'park', 'outdoor', 'flowers', 'nursery', 'wellness', 'calm', 'tree', 'trees', 'palm', 'leaf', 'leaves', 'shrub', 'path', 'farm'],
  craft: ['craft', 'handmade', 'design', 'art', 'artisan', 'interior', 'furniture', 'detail', 'pattern', 'carpentry', 'gift', 'wood', 'wooden', 'carved', 'door', 'carving', 'geometric', 'workshop', 'atelier'],
  desert: ['desert', 'travel', 'tour', 'tours', 'adventure', 'trip', 'outdoors', 'camping', 'sand', 'dune', 'dunes', 'camel', 'camp', 'sunset', 'horizon', 'safari'],
  clinic: ['clinic', 'medical', 'dental', 'dentist', 'health', 'care', 'treatment', 'therapy', 'wellbeing', 'waiting', 'reception', 'hygiene', 'patient', 'smile', 'teeth', 'pharmacy'],
  bakery: ['bakery', 'sweets', 'dates', 'maamoul', 'pastry', 'cake', 'cakes', 'baked', 'chocolate', 'biscuits', 'confectionery', 'gifts', 'box'],
  fitness: ['gym', 'fitness', 'training', 'trainer', 'workout', 'exercise', 'weights', 'dumbbells', 'sport', 'sports', 'yoga', 'coaching'],
  school: ['school', 'nursery', 'kindergarten', 'academy', 'education', 'teaching', 'classroom', 'lesson', 'lessons', 'students', 'tutoring', 'courses', 'learning', 'children', 'kids'],
  salon: ['salon', 'barber', 'haircut', 'hair', 'grooming', 'beauty', 'spa', 'nails', 'makeup', 'shave', 'styling', 'chair'],
  flowers: ['flowers', 'florist', 'bouquet', 'roses', 'arrangement', 'wreath', 'petals', 'bloom', 'gift', 'wrapping'],
  property: ['property', 'real', 'estate', 'villa', 'apartment', 'house', 'housing', 'rent', 'lease', 'building', 'homes', 'agency'],
  events: ['event', 'events', 'wedding', 'reception', 'party', 'celebration', 'catering', 'hall', 'venue', 'occasion', 'planning', 'decor'],
  construction: ['construction', 'contracting', 'contractor', 'building', 'builder', 'renovation', 'maintenance', 'plumbing', 'electrical', 'materials', 'site', 'tools', 'scaffolding'],
}

/**
 * The same table in Arabic, and it is a **table of its own rather than a translation**.
 *
 * > ⚠️ **THE MATCHER WAS ENGLISH-ONLY AND HALF THIS PRODUCT IS ARABIC, SO HALF OF IT NEVER WORKED.**
 * > It scores the surrounding block's rendered text — which on an Arabic page is Arabic — against
 * > tags that are all English. Every picture scored zero, every slot fell through to the stable
 * > pseudo-random pick, and a mobile phone repair shop in Granada was given a photograph of a
 * > barber's chair. `pickPlaceholder`'s own doc said "English is used because the tags are English;
 * > the Arabic description describes the same photograph, so nothing is lost" — which was true of
 * > the model's bilingual DESCRIPTION field, and stopped being true twice over: that field is gone
 * > (the words come from the page now), and a stranger's site is written in ONE language on purpose
 * > (§5.15), which for this market is usually Arabic. Found in a screenshot, like the other three.
 *
 * ⚠️ TRANSLATING THE ENGLISH LIST WOULD HAVE BEEN THE WRONG ARTEFACT, for the reason the English one
 * already records: these are the words copy actually uses. «dining» translated is not what a Saudi
 * restaurant's page says; «مشاوي» and «كبسة» are.
 *
 * ⚠️ AND THEY ARE MATCHED AS SUBSTRINGS, NOT AS WHOLE WORDS. Arabic glues its conjunctions and its
 * article to the front of a word — «أغطية» arrives as «وأغطية», «الجوال» as «لخدمة الجوال» — so the
 * space-delimited match the English side uses finds almost nothing. Every entry below is therefore
 * long enough to be distinctive on its own; a two-letter Arabic tag would match half the language.
 */
const TAGS_AR: Record<string, string[]> = {
  coffee: ['قهوه', 'مقهي', 'كافيه', 'اسبريسو', 'لاتيه', 'محمصه', 'شاي', 'مشروبات', 'باريستا', 'حبوب'],
  restaurant: ['مطعم', 'طعام', 'وجبه', 'وجبات', 'طبق', 'اطباق', 'مطبخ', 'غداء', 'عشاء', 'سمك', 'دجاج', 'لحم', 'مشاوي', 'مشوي', 'خبز', 'عصير', 'فطور', 'كبسه', 'ماكولات'],
  retail: ['متجر', 'محل', 'تسوق', 'منتجات', 'اكسسوارات', 'جوال', 'هاتف', 'هواتف', 'موبايل', 'الكترونيات', 'رفوف', 'اغطيه', 'شاحن', 'شواحن', 'شاشات', 'شاشه', 'بطاريات', 'بطاريه'],
  car: ['سياره', 'سيارات', 'مركبه', 'تاجير', 'نقل', 'توصيل', 'اطارات', 'محرك', 'مواقف'],
  tower: ['مكتب', 'اعمال', 'شركه', 'استشارات', 'ماليه', 'وكاله', 'الرياض', 'مدينه', 'اجتماع', 'فريق', 'خدمات'],
  diriyah: ['تراث', 'ثقافه', 'تقليدي', 'تاريخ', 'متحف', 'سياحه', 'الدرعيه', 'قريه', 'قلعه', 'قديم'],
  greenery: ['حديقه', 'نباتات', 'مشتل', 'طبيعه', 'اخضر', 'شجره', 'اشجار', 'نخيل', 'مزرعه'],
  craft: ['حرفيه', 'يدوي', 'تصميم', 'اثاث', 'نقش', 'خشب', 'خشبي', 'حفر', 'زخرفه'],
  desert: ['صحراء', 'رحله', 'رحلات', 'مغامره', 'تخييم', 'رمال', 'كثبان', 'جمال', 'مخيم', 'غروب', 'سفاري'],
  clinic: ['عياده', 'طبي', 'اسنان', 'طبيب', 'صحه', 'علاج', 'رعايه', 'حشوات', 'تقويم', 'مواعيد', 'مريض', 'ابتسامه', 'صيدليه'],
  bakery: ['مخبز', 'حلويات', 'تمر', 'معمول', 'معجنات', 'كيك', 'كعك', 'شوكولاته', 'بسكويت'],
  fitness: ['لياقه', 'تدريب', 'مدرب', 'تمارين', 'رياضه', 'يوغا', 'اوزان', 'جيم'],
  school: ['مدرسه', 'حضانه', 'روضه', 'اكاديميه', 'تعليم', 'تدريس', 'دروس', 'طلاب', 'اطفال', 'دورات'],
  salon: ['صالون', 'حلاق', 'حلاقه', 'شعر', 'تجميل', 'سبا', 'اظافر', 'مكياج'],
  flowers: ['ورود', 'زهور', 'باقه', 'باقات', 'تنسيق', 'مناسبات'],
  property: ['عقار', 'عقارات', 'فيلا', 'شقه', 'شقق', 'منزل', 'سكن', 'ايجار', 'مبني'],
  events: ['حفل', 'زفاف', 'اعراس', 'قاعه', 'تنظيم', 'ديكور'],
  construction: ['مقاولات', 'بناء', 'انشاءات', 'ترميم', 'صيانه', 'سباكه', 'كهرباء', 'ادوات'],
}

/**
 * Arabic as one spelling, so a tag and the page agree.
 *
 * ⚠️ WITHOUT THIS, «أسنان» AND «اسنان» ARE DIFFERENT STRINGS AND ONE OF THEM MATCHES NOTHING. Arabic
 * copy is written with whichever hamza carrier, ya and ta-marbuta the writer prefers, and a model
 * writes it differently from a customer correcting it — so the only reliable comparison is on a
 * flattened form. Tashkeel and tatweel go for the same reason: they are decoration a page may or may
 * not carry, and neither changes the word.
 */
function flattenArabic(text: string): string {
  return text
    .replace(/[\u064B-\u0652\u0670\u0640]/g, '')
    .replace(/[\u0622\u0623\u0625\u0671]/g, '\u0627')
    .replace(/\u0649/g, '\u064A')
    .replace(/\u0629/g, '\u0647')
    .replace(/\u0624/g, '\u0648')
    .replace(/\u0626/g, '\u064A')
}

/**
 * ⚠️ BUILT FROM THE PICTURES, NOT FROM THE TAGS, SO A PICTURE WITHOUT TAGS STILL EXISTS. A newly
 * generated image that nobody has tagged yet should be reachable by the random fallback rather than
 * silently absent — and `placeholders.test.ts` fails on the mismatch so it does not stay untagged.
 * `GALLERY_IDS` is generated beside the pixels, so the two cannot disagree about what exists.
 */
export const GALLERY: Placeholder[] = GALLERY_IDS.map((id) => ({
  id,
  tags: TAGS[id] ?? [],
  tagsAr: (TAGS_AR[id] ?? []).map(flattenArabic),
}))

/**
 * The pixels, on demand.
 *
 * ⚠️ A DYNAMIC IMPORT, AND THE PROMISE IS CACHED RATHER THAN THE MODULE RE-IMPORTED. Every image
 * slot on the page asks for this at once on first paint; without the cache that is one `import()`
 * per slot, and while the bundler does dedupe the request, the promise chain per slot is not free
 * and the code reads as though it might fetch a dozen times.
 *
 * ⚠️ IT RESOLVES TO AN EMPTY MAP RATHER THAN THROWING. The consequence of the chunk failing to load
 * is an editor with grey boxes where the stand-in pictures would be — which is the state this
 * feature improves on, not a broken editor.
 */
let artPromise: Promise<Record<string, string>> | null = null

export function loadPlaceholderArt(): Promise<Record<string, string>> {
  artPromise ??= import('./gallery.generated.ts').then(
    (module) => module.ART,
    () => ({}),
  )
  return artPromise
}

/**
 * ⚠️ A STABLE PSEUDO-RANDOM PICK, NOT `Math.random()`. A description that matches nothing must get
 * the SAME picture every render, or the preview reshuffles its own artwork on every keystroke —
 * which reads as the page loading rather than as a placeholder.
 */
function hash(seed: string): number {
  let h = 2166136261
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  return Math.abs(h)
}

/**
 * Pick a placeholder for an image slot.
 *
 * > ⚠️ **THIS SAID "ENGLISH IS USED BECAUSE THE TAGS ARE ENGLISH; THE ARABIC DESCRIPTION DESCRIBES
 * > THE SAME PHOTOGRAPH, SO NOTHING IS LOST BY NOT SCORING IT."** That was true while the model
 * > wrote a bilingual `description` field for every slot, and it has been false twice over since:
 * > the field is gone (the words come from the surrounding block now, in whatever language is on
 * > screen) and a stranger's site is written in ONE language on purpose (§5.15), which in this
 * > market is usually Arabic. The sentence's own escape clause — "a second, translated tag table
 * > that would drift" — is answered by `TAGS_AR` being written from Arabic copy rather than
 * > translated from the English list.
 *
 * `description` is whatever words surround the slot, in either language. Both tables are scored and
 * the scores are summed, because a bilingual page has both and neither is the wrong one to read.
 *
 * `seed` should be stable for the slot (the block id and field key), so an unmatched slot keeps its
 * picture across edits.
 */
export function pickPlaceholder(description: string, seed: string): Placeholder {
  const text = ` ${description.toLowerCase().replace(/[^a-z0-9]+/g, ' ')} `
  /*
   * ⚠️ THE ARABIC PASS KEEPS THE ARABIC. The English normalisation above strips every non-ASCII
   * character, so running one string through both is a string with no Arabic left in it — which is
   * precisely how a whole language scores zero without anything looking wrong.
   */
  const arabic = flattenArabic(description)

  let best: Placeholder | null = null
  let bestScore = 0

  for (const entry of GALLERY) {
    let score = 0
    for (const tag of entry.tags) {
      if (text.includes(` ${tag} `)) score += 1
    }
    for (const tag of entry.tagsAr) {
      if (arabic.includes(tag)) score += 1
    }
    // ⚠️ STRICTLY GREATER, SO A TIE KEEPS THE EARLIER ENTRY. `GALLERY` order is therefore a
    // deliberate preference order rather than an accident of the config file.
    if (score > bestScore) {
      bestScore = score
      best = entry
    }
  }

  if (best) return best

  /**
   * ⚠️ THE DESCRIPTION IS THE SEED, NOT THE SLOT. This seeded on the slot's position, and a
   * screenshot caught what that does: three gallery items sharing one description — "a plate of
   * grilled fish and rice", three times — were handed three unrelated pictures, so a row that is one
   * idea looked like three. Seeding on the text means identical descriptions get identical pictures
   * and different ones still differ, which is the property that was wanted in the first place.
   *
   * The slot seed survives only for an EMPTY description, where there is nothing else to go on and
   * the alternative is every unfilled box on the page showing the same picture.
   */
  return GALLERY[hash(description.trim() || seed) % GALLERY.length]!
}
